/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AddProjectMemberPathParameters,
  GetProjectByIdPathParameters,
  GetProjectMembersParams,
  GetProjectMembersPathParameters,
  GetProjectsParams,
  RemoveProjectMemberPathParameters,
  RenameProjectParams,
  RenameProjectPathParameters,
  TLAddProjectMemberRequest,
  TLBulkDeleteProjectsRequest,
  TLBulkDeleteProjectsResponse,
  TLCreateProjectRequest,
  TLHTTPValidationError,
  TLMoveProgramsRequest,
  TLMoveProgramsResponse,
  TLPaginatedResponseProjectMember,
  TLProject,
  TLProjectMember,
  TLProjectsListResponse,
  TLRenameProjectResponse,
  TLUpdateProjectMemberRoleRequest,
  TLUpdateProjectSystemsRequest,
  UpdateProjectMemberRolePathParameters,
  UpdateProjectSystemsPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Projects
 */
export const getProjects = (
  params?: GetProjectsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProjectsListResponse>({ url: `/projects`, method: 'GET', params, signal }, options);
};

export const getGetProjectsQueryKey = (params?: GetProjectsParams) => {
  return ['projects', ...(params ? [params] : [])] as const;
};

export const getGetProjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof getProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetProjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProjectsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjects>>> = ({ signal }) =>
    getProjects(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProjects>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProjectsQueryResult = NonNullable<Awaited<ReturnType<typeof getProjects>>>;
export type GetProjectsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProjects<
  TData = Awaited<ReturnType<typeof getProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetProjectsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjects>>,
          TError,
          Awaited<ReturnType<typeof getProjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjects<
  TData = Awaited<ReturnType<typeof getProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetProjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjects>>,
          TError,
          Awaited<ReturnType<typeof getProjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjects<
  TData = Awaited<ReturnType<typeof getProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetProjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Projects
 */

export function useGetProjects<
  TData = Awaited<ReturnType<typeof getProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetProjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProjectsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Projects
 */
export const prefetchGetProjectsQuery = async <
  TData = Awaited<ReturnType<typeof getProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetProjectsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProjectsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Projects
 */
export const invalidateGetProjects = async (
  queryClient: QueryClient,
  params?: GetProjectsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetProjectsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Project
 */
export const createProject = (
  tLCreateProjectRequest: BodyType<TLCreateProjectRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProject>(
    {
      url: `/projects`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateProjectRequest,
      signal,
    },
    options,
  );
};

export const getCreateProjectQueryKey = (tLCreateProjectRequest?: BodyType<TLCreateProjectRequest>) => {
  return ['POST', 'projects', tLCreateProjectRequest] as const;
};

export const getCreateProjectQueryOptions = <
  TData = Awaited<ReturnType<typeof createProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProjectRequest: BodyType<TLCreateProjectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateProjectQueryKey(tLCreateProjectRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createProject>>> = ({ signal }) =>
    createProject(tLCreateProjectRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createProject>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateProjectQueryResult = NonNullable<Awaited<ReturnType<typeof createProject>>>;
export type CreateProjectQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateProject<
  TData = Awaited<ReturnType<typeof createProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProjectRequest: BodyType<TLCreateProjectRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProject>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createProject>>,
          TError,
          Awaited<ReturnType<typeof createProject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateProject<
  TData = Awaited<ReturnType<typeof createProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProjectRequest: BodyType<TLCreateProjectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProject>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createProject>>,
          TError,
          Awaited<ReturnType<typeof createProject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateProject<
  TData = Awaited<ReturnType<typeof createProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProjectRequest: BodyType<TLCreateProjectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Project
 */

export function useCreateProject<
  TData = Awaited<ReturnType<typeof createProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateProjectRequest: BodyType<TLCreateProjectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateProjectQueryOptions(tLCreateProjectRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Project
 */
export const prefetchCreateProjectQuery = async <
  TData = Awaited<ReturnType<typeof createProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCreateProjectRequest: BodyType<TLCreateProjectRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateProjectQueryOptions(tLCreateProjectRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Project
 */
export const invalidateCreateProject = async (
  queryClient: QueryClient,
  tLCreateProjectRequest: BodyType<TLCreateProjectRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateProjectQueryKey(tLCreateProjectRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Projects
 */
export const deleteProjects = (
  tLBulkDeleteProjectsRequest: BodyType<TLBulkDeleteProjectsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkDeleteProjectsResponse>(
    {
      url: `/projects/delete`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLBulkDeleteProjectsRequest,
      signal,
    },
    options,
  );
};

export const getDeleteProjectsQueryKey = (tLBulkDeleteProjectsRequest?: BodyType<TLBulkDeleteProjectsRequest>) => {
  return ['POST', 'projects', 'delete', tLBulkDeleteProjectsRequest] as const;
};

export const getDeleteProjectsQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteProjectsRequest: BodyType<TLBulkDeleteProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteProjectsQueryKey(tLBulkDeleteProjectsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteProjects>>> = ({ signal }) =>
    deleteProjects(tLBulkDeleteProjectsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteProjects>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteProjectsQueryResult = NonNullable<Awaited<ReturnType<typeof deleteProjects>>>;
export type DeleteProjectsQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteProjects<
  TData = Awaited<ReturnType<typeof deleteProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteProjectsRequest: BodyType<TLBulkDeleteProjectsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProjects>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteProjects>>,
          TError,
          Awaited<ReturnType<typeof deleteProjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteProjects<
  TData = Awaited<ReturnType<typeof deleteProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteProjectsRequest: BodyType<TLBulkDeleteProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProjects>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteProjects>>,
          TError,
          Awaited<ReturnType<typeof deleteProjects>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteProjects<
  TData = Awaited<ReturnType<typeof deleteProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteProjectsRequest: BodyType<TLBulkDeleteProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Projects
 */

export function useDeleteProjects<
  TData = Awaited<ReturnType<typeof deleteProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLBulkDeleteProjectsRequest: BodyType<TLBulkDeleteProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteProjectsQueryOptions(tLBulkDeleteProjectsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Projects
 */
export const prefetchDeleteProjectsQuery = async <
  TData = Awaited<ReturnType<typeof deleteProjects>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLBulkDeleteProjectsRequest: BodyType<TLBulkDeleteProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteProjects>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteProjectsQueryOptions(tLBulkDeleteProjectsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Projects
 */
export const invalidateDeleteProjects = async (
  queryClient: QueryClient,
  tLBulkDeleteProjectsRequest: BodyType<TLBulkDeleteProjectsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteProjectsQueryKey(tLBulkDeleteProjectsRequest) }, options);

  return queryClient;
};

/**
 * @summary Move Programs
 */
export const movePrograms = (
  tLMoveProgramsRequest: BodyType<TLMoveProgramsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMoveProgramsResponse>(
    {
      url: `/projects/move-programs`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLMoveProgramsRequest,
      signal,
    },
    options,
  );
};

export const getMoveProgramsQueryKey = (tLMoveProgramsRequest?: BodyType<TLMoveProgramsRequest>) => {
  return ['POST', 'projects', 'move-programs', tLMoveProgramsRequest] as const;
};

export const getMoveProgramsQueryOptions = <
  TData = Awaited<ReturnType<typeof movePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMoveProgramsRequest: BodyType<TLMoveProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof movePrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMoveProgramsQueryKey(tLMoveProgramsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof movePrograms>>> = ({ signal }) =>
    movePrograms(tLMoveProgramsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof movePrograms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MoveProgramsQueryResult = NonNullable<Awaited<ReturnType<typeof movePrograms>>>;
export type MoveProgramsQueryError = ErrorType<TLHTTPValidationError>;

export function useMovePrograms<
  TData = Awaited<ReturnType<typeof movePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMoveProgramsRequest: BodyType<TLMoveProgramsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof movePrograms>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof movePrograms>>,
          TError,
          Awaited<ReturnType<typeof movePrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMovePrograms<
  TData = Awaited<ReturnType<typeof movePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMoveProgramsRequest: BodyType<TLMoveProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof movePrograms>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof movePrograms>>,
          TError,
          Awaited<ReturnType<typeof movePrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMovePrograms<
  TData = Awaited<ReturnType<typeof movePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMoveProgramsRequest: BodyType<TLMoveProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof movePrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Move Programs
 */

export function useMovePrograms<
  TData = Awaited<ReturnType<typeof movePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMoveProgramsRequest: BodyType<TLMoveProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof movePrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMoveProgramsQueryOptions(tLMoveProgramsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Move Programs
 */
export const prefetchMoveProgramsQuery = async <
  TData = Awaited<ReturnType<typeof movePrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLMoveProgramsRequest: BodyType<TLMoveProgramsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof movePrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMoveProgramsQueryOptions(tLMoveProgramsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Move Programs
 */
export const invalidateMovePrograms = async (
  queryClient: QueryClient,
  tLMoveProgramsRequest: BodyType<TLMoveProgramsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMoveProgramsQueryKey(tLMoveProgramsRequest) }, options);

  return queryClient;
};

/**
 * @summary Rename Project
 */
export const renameProject = (
  { projectId }: RenameProjectPathParameters,
  params: RenameProjectParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRenameProjectResponse>(
    { url: `/projects/rename/${projectId}`, method: 'POST', params, signal },
    options,
  );
};

export const getRenameProjectQueryKey = ({ projectId }: RenameProjectPathParameters, params?: RenameProjectParams) => {
  return ['POST', 'projects', 'rename', projectId, ...(params ? [params] : [])] as const;
};

export const getRenameProjectQueryOptions = <
  TData = Awaited<ReturnType<typeof renameProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: RenameProjectPathParameters,
  params: RenameProjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRenameProjectQueryKey({ projectId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof renameProject>>> = ({ signal }) =>
    renameProject({ projectId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof renameProject>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RenameProjectQueryResult = NonNullable<Awaited<ReturnType<typeof renameProject>>>;
export type RenameProjectQueryError = ErrorType<TLHTTPValidationError>;

export function useRenameProject<
  TData = Awaited<ReturnType<typeof renameProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameProjectPathParameters,
  params: RenameProjectParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProject>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof renameProject>>,
          TError,
          Awaited<ReturnType<typeof renameProject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRenameProject<
  TData = Awaited<ReturnType<typeof renameProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameProjectPathParameters,
  params: RenameProjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProject>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof renameProject>>,
          TError,
          Awaited<ReturnType<typeof renameProject>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRenameProject<
  TData = Awaited<ReturnType<typeof renameProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameProjectPathParameters,
  params: RenameProjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Rename Project
 */

export function useRenameProject<
  TData = Awaited<ReturnType<typeof renameProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: RenameProjectPathParameters,
  params: RenameProjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRenameProjectQueryOptions({ projectId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Rename Project
 */
export const prefetchRenameProjectQuery = async <
  TData = Awaited<ReturnType<typeof renameProject>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: RenameProjectPathParameters,
  params: RenameProjectParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameProject>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRenameProjectQueryOptions({ projectId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Rename Project
 */
export const invalidateRenameProject = async (
  queryClient: QueryClient,
  { projectId }: RenameProjectPathParameters,
  params: RenameProjectParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRenameProjectQueryKey({ projectId }, params) }, options);

  return queryClient;
};

/**
 * @summary Get Project By Id
 */
export const getProjectById = (
  { projectId }: GetProjectByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProject>({ url: `/projects/${projectId}`, method: 'GET', signal }, options);
};

export const getGetProjectByIdQueryKey = ({ projectId }: GetProjectByIdPathParameters) => {
  return ['projects', projectId] as const;
};

export const getGetProjectByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getProjectById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetProjectByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProjectByIdQueryKey({ projectId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectById>>> = ({ signal }) =>
    getProjectById({ projectId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProjectByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getProjectById>>>;
export type GetProjectByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProjectById<
  TData = Awaited<ReturnType<typeof getProjectById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProjectByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjectById>>,
          TError,
          Awaited<ReturnType<typeof getProjectById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectById<
  TData = Awaited<ReturnType<typeof getProjectById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProjectByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjectById>>,
          TError,
          Awaited<ReturnType<typeof getProjectById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectById<
  TData = Awaited<ReturnType<typeof getProjectById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProjectByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Project By Id
 */

export function useGetProjectById<
  TData = Awaited<ReturnType<typeof getProjectById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetProjectByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProjectByIdQueryOptions({ projectId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Project By Id
 */
export const prefetchGetProjectByIdQuery = async <
  TData = Awaited<ReturnType<typeof getProjectById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: GetProjectByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProjectByIdQueryOptions({ projectId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Project By Id
 */
export const invalidateGetProjectById = async (
  queryClient: QueryClient,
  { projectId }: GetProjectByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetProjectByIdQueryKey({ projectId }) }, options);

  return queryClient;
};

/**
 * @summary Get Project Members
 */
export const getProjectMembers = (
  { projectId }: GetProjectMembersPathParameters,
  params?: GetProjectMembersParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseProjectMember>(
    { url: `/projects/${projectId}/members`, method: 'GET', params, signal },
    options,
  );
};

export const getGetProjectMembersQueryKey = (
  { projectId }: GetProjectMembersPathParameters,
  params?: GetProjectMembersParams,
) => {
  return ['projects', projectId, 'members', ...(params ? [params] : [])] as const;
};

export const getGetProjectMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof getProjectMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetProjectMembersPathParameters,
  params?: GetProjectMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProjectMembersQueryKey({ projectId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectMembers>>> = ({ signal }) =>
    getProjectMembers({ projectId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getProjectMembers>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProjectMembersQueryResult = NonNullable<Awaited<ReturnType<typeof getProjectMembers>>>;
export type GetProjectMembersQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProjectMembers<
  TData = Awaited<ReturnType<typeof getProjectMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProjectMembersPathParameters,
  params: undefined | GetProjectMembersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectMembers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjectMembers>>,
          TError,
          Awaited<ReturnType<typeof getProjectMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectMembers<
  TData = Awaited<ReturnType<typeof getProjectMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProjectMembersPathParameters,
  params?: GetProjectMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectMembers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjectMembers>>,
          TError,
          Awaited<ReturnType<typeof getProjectMembers>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectMembers<
  TData = Awaited<ReturnType<typeof getProjectMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProjectMembersPathParameters,
  params?: GetProjectMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Project Members
 */

export function useGetProjectMembers<
  TData = Awaited<ReturnType<typeof getProjectMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetProjectMembersPathParameters,
  params?: GetProjectMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProjectMembersQueryOptions({ projectId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Project Members
 */
export const prefetchGetProjectMembersQuery = async <
  TData = Awaited<ReturnType<typeof getProjectMembers>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: GetProjectMembersPathParameters,
  params?: GetProjectMembersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProjectMembers>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProjectMembersQueryOptions({ projectId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Project Members
 */
export const invalidateGetProjectMembers = async (
  queryClient: QueryClient,
  { projectId }: GetProjectMembersPathParameters,
  params?: GetProjectMembersParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetProjectMembersQueryKey({ projectId }, params) }, options);

  return queryClient;
};

/**
 * @summary Add Project Member
 */
export const addProjectMember = (
  { projectId }: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest: BodyType<TLAddProjectMemberRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProjectMember>(
    {
      url: `/projects/${projectId}/members`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAddProjectMemberRequest,
      signal,
    },
    options,
  );
};

export const getAddProjectMemberQueryKey = (
  { projectId }: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest?: BodyType<TLAddProjectMemberRequest>,
) => {
  return ['POST', 'projects', projectId, 'members', tLAddProjectMemberRequest] as const;
};

export const getAddProjectMemberQueryOptions = <
  TData = Awaited<ReturnType<typeof addProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest: BodyType<TLAddProjectMemberRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addProjectMember>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAddProjectMemberQueryKey({ projectId }, tLAddProjectMemberRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addProjectMember>>> = ({ signal }) =>
    addProjectMember({ projectId }, tLAddProjectMemberRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addProjectMember>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddProjectMemberQueryResult = NonNullable<Awaited<ReturnType<typeof addProjectMember>>>;
export type AddProjectMemberQueryError = ErrorType<TLHTTPValidationError>;

export function useAddProjectMember<
  TData = Awaited<ReturnType<typeof addProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest: BodyType<TLAddProjectMemberRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addProjectMember>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof addProjectMember>>,
          TError,
          Awaited<ReturnType<typeof addProjectMember>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddProjectMember<
  TData = Awaited<ReturnType<typeof addProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest: BodyType<TLAddProjectMemberRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addProjectMember>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addProjectMember>>,
          TError,
          Awaited<ReturnType<typeof addProjectMember>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddProjectMember<
  TData = Awaited<ReturnType<typeof addProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest: BodyType<TLAddProjectMemberRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addProjectMember>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Project Member
 */

export function useAddProjectMember<
  TData = Awaited<ReturnType<typeof addProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest: BodyType<TLAddProjectMemberRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addProjectMember>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddProjectMemberQueryOptions({ projectId }, tLAddProjectMemberRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Project Member
 */
export const prefetchAddProjectMemberQuery = async <
  TData = Awaited<ReturnType<typeof addProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest: BodyType<TLAddProjectMemberRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addProjectMember>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddProjectMemberQueryOptions({ projectId }, tLAddProjectMemberRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Project Member
 */
export const invalidateAddProjectMember = async (
  queryClient: QueryClient,
  { projectId }: AddProjectMemberPathParameters,
  tLAddProjectMemberRequest: BodyType<TLAddProjectMemberRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddProjectMemberQueryKey({ projectId }, tLAddProjectMemberRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Remove Project Member
 */
export const removeProjectMember = (
  { projectId, userEmail }: RemoveProjectMemberPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/projects/${projectId}/members/${userEmail}`, method: 'DELETE', signal }, options);
};

export const getRemoveProjectMemberQueryKey = ({ projectId, userEmail }: RemoveProjectMemberPathParameters) => {
  return ['DELETE', 'projects', projectId, 'members', userEmail] as const;
};

export const getRemoveProjectMemberQueryOptions = <
  TData = Awaited<ReturnType<typeof removeProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, userEmail }: RemoveProjectMemberPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeProjectMember>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRemoveProjectMemberQueryKey({ projectId, userEmail });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof removeProjectMember>>> = ({ signal }) =>
    removeProjectMember({ projectId, userEmail }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && userEmail !== null && userEmail !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof removeProjectMember>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RemoveProjectMemberQueryResult = NonNullable<Awaited<ReturnType<typeof removeProjectMember>>>;
export type RemoveProjectMemberQueryError = ErrorType<TLHTTPValidationError>;

export function useRemoveProjectMember<
  TData = Awaited<ReturnType<typeof removeProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveProjectMemberPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeProjectMember>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeProjectMember>>,
          TError,
          Awaited<ReturnType<typeof removeProjectMember>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveProjectMember<
  TData = Awaited<ReturnType<typeof removeProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveProjectMemberPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeProjectMember>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeProjectMember>>,
          TError,
          Awaited<ReturnType<typeof removeProjectMember>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveProjectMember<
  TData = Awaited<ReturnType<typeof removeProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveProjectMemberPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeProjectMember>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove Project Member
 */

export function useRemoveProjectMember<
  TData = Awaited<ReturnType<typeof removeProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, userEmail }: RemoveProjectMemberPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeProjectMember>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemoveProjectMemberQueryOptions({ projectId, userEmail }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remove Project Member
 */
export const prefetchRemoveProjectMemberQuery = async <
  TData = Awaited<ReturnType<typeof removeProjectMember>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, userEmail }: RemoveProjectMemberPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeProjectMember>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemoveProjectMemberQueryOptions({ projectId, userEmail }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remove Project Member
 */
export const invalidateRemoveProjectMember = async (
  queryClient: QueryClient,
  { projectId, userEmail }: RemoveProjectMemberPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRemoveProjectMemberQueryKey({ projectId, userEmail }) }, options);

  return queryClient;
};

/**
 * @summary Update Project Member Role
 */
export const updateProjectMemberRole = (
  { projectId, userEmail }: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest: BodyType<TLUpdateProjectMemberRoleRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProjectMember>(
    {
      url: `/projects/${projectId}/members/${userEmail}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateProjectMemberRoleRequest,
      signal,
    },
    options,
  );
};

export const getUpdateProjectMemberRoleQueryKey = (
  { projectId, userEmail }: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest?: BodyType<TLUpdateProjectMemberRoleRequest>,
) => {
  return ['PATCH', 'projects', projectId, 'members', userEmail, tLUpdateProjectMemberRoleRequest] as const;
};

export const getUpdateProjectMemberRoleQueryOptions = <
  TData = Awaited<ReturnType<typeof updateProjectMemberRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, userEmail }: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest: BodyType<TLUpdateProjectMemberRoleRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectMemberRole>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateProjectMemberRoleQueryKey({ projectId, userEmail }, tLUpdateProjectMemberRoleRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateProjectMemberRole>>> = ({ signal }) =>
    updateProjectMemberRole({ projectId, userEmail }, tLUpdateProjectMemberRoleRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && userEmail !== null && userEmail !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateProjectMemberRole>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateProjectMemberRoleQueryResult = NonNullable<Awaited<ReturnType<typeof updateProjectMemberRole>>>;
export type UpdateProjectMemberRoleQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateProjectMemberRole<
  TData = Awaited<ReturnType<typeof updateProjectMemberRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest: BodyType<TLUpdateProjectMemberRoleRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectMemberRole>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateProjectMemberRole>>,
          TError,
          Awaited<ReturnType<typeof updateProjectMemberRole>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateProjectMemberRole<
  TData = Awaited<ReturnType<typeof updateProjectMemberRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest: BodyType<TLUpdateProjectMemberRoleRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectMemberRole>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateProjectMemberRole>>,
          TError,
          Awaited<ReturnType<typeof updateProjectMemberRole>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateProjectMemberRole<
  TData = Awaited<ReturnType<typeof updateProjectMemberRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest: BodyType<TLUpdateProjectMemberRoleRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectMemberRole>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Project Member Role
 */

export function useUpdateProjectMemberRole<
  TData = Awaited<ReturnType<typeof updateProjectMemberRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, userEmail }: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest: BodyType<TLUpdateProjectMemberRoleRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectMemberRole>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateProjectMemberRoleQueryOptions(
    { projectId, userEmail },
    tLUpdateProjectMemberRoleRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Project Member Role
 */
export const prefetchUpdateProjectMemberRoleQuery = async <
  TData = Awaited<ReturnType<typeof updateProjectMemberRole>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, userEmail }: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest: BodyType<TLUpdateProjectMemberRoleRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectMemberRole>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateProjectMemberRoleQueryOptions(
    { projectId, userEmail },
    tLUpdateProjectMemberRoleRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Project Member Role
 */
export const invalidateUpdateProjectMemberRole = async (
  queryClient: QueryClient,
  { projectId, userEmail }: UpdateProjectMemberRolePathParameters,
  tLUpdateProjectMemberRoleRequest: BodyType<TLUpdateProjectMemberRoleRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateProjectMemberRoleQueryKey({ projectId, userEmail }, tLUpdateProjectMemberRoleRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Project Systems
 */
export const updateProjectSystems = (
  { projectId }: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest: BodyType<TLUpdateProjectSystemsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProject>(
    {
      url: `/projects/${projectId}/systems`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateProjectSystemsRequest,
      signal,
    },
    options,
  );
};

export const getUpdateProjectSystemsQueryKey = (
  { projectId }: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest?: BodyType<TLUpdateProjectSystemsRequest>,
) => {
  return ['PATCH', 'projects', projectId, 'systems', tLUpdateProjectSystemsRequest] as const;
};

export const getUpdateProjectSystemsQueryOptions = <
  TData = Awaited<ReturnType<typeof updateProjectSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest: BodyType<TLUpdateProjectSystemsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectSystems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateProjectSystemsQueryKey({ projectId }, tLUpdateProjectSystemsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateProjectSystems>>> = ({ signal }) =>
    updateProjectSystems({ projectId }, tLUpdateProjectSystemsRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateProjectSystems>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateProjectSystemsQueryResult = NonNullable<Awaited<ReturnType<typeof updateProjectSystems>>>;
export type UpdateProjectSystemsQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateProjectSystems<
  TData = Awaited<ReturnType<typeof updateProjectSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest: BodyType<TLUpdateProjectSystemsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectSystems>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateProjectSystems>>,
          TError,
          Awaited<ReturnType<typeof updateProjectSystems>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateProjectSystems<
  TData = Awaited<ReturnType<typeof updateProjectSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest: BodyType<TLUpdateProjectSystemsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectSystems>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateProjectSystems>>,
          TError,
          Awaited<ReturnType<typeof updateProjectSystems>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateProjectSystems<
  TData = Awaited<ReturnType<typeof updateProjectSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest: BodyType<TLUpdateProjectSystemsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectSystems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Project Systems
 */

export function useUpdateProjectSystems<
  TData = Awaited<ReturnType<typeof updateProjectSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest: BodyType<TLUpdateProjectSystemsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectSystems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateProjectSystemsQueryOptions({ projectId }, tLUpdateProjectSystemsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Project Systems
 */
export const prefetchUpdateProjectSystemsQuery = async <
  TData = Awaited<ReturnType<typeof updateProjectSystems>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest: BodyType<TLUpdateProjectSystemsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateProjectSystems>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateProjectSystemsQueryOptions({ projectId }, tLUpdateProjectSystemsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Project Systems
 */
export const invalidateUpdateProjectSystems = async (
  queryClient: QueryClient,
  { projectId }: UpdateProjectSystemsPathParameters,
  tLUpdateProjectSystemsRequest: BodyType<TLUpdateProjectSystemsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateProjectSystemsQueryKey({ projectId }, tLUpdateProjectSystemsRequest) },
    options,
  );

  return queryClient;
};
